import React, { useEffect, useState } from 'react';
import api, { extrairMensagemErro } from '../services/api';
import Paginacao, { MetadadosPaginacao, paginacaoVazia } from '../components/Paginacao';

const perfis = [
  { valor: 'SOLICITANTE', label: 'Solicitante' },
  { valor: 'APROVADOR', label: 'Aprovador' },
  { valor: 'COMPRADOR', label: 'Comprador' },
  { valor: 'FIEL_ARMAZEM', label: 'Armazém' }
];

const perfisComAlcada = new Set(['APROVADOR', 'COMPRADOR']);
const LIMITE_NIVEL4_ILIMITADO = 999_999_999_999_999;

const formMembroVazio = {
  nome: '',
  email: '',
  perfil: 'SOLICITANTE',
  nivel_alcada: 1,
  status: 'ATIVO',
  senha: ''
};

const alcadasVazias = {
  alcada_nivel1_max: 50000,
  alcada_nivel2_max: 100000,
  alcada_nivel3_max: 500000,
  alcada_nivel4_max: 1000000,
  nivel4_ilimitado: false
};

const moeda = (valor: number) =>
  `KZ ${Math.round(Number(valor || 0)).toLocaleString('pt-PT')}`;

const EquipaInterna: React.FC = () => {
  const [membros, setMembros] = useState<any[]>([]);
  const [form, setForm] = useState(formMembroVazio);
  const [membroEditandoId, setMembroEditandoId] = useState<string | null>(null);
  const [edicao, setEdicao] = useState<any>({});
  const [alcadas, setAlcadas] = useState(alcadasVazias);
  const [erro, setErro] = useState<string | null>(null);
  const [mensagem, setMensagem] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [paginacao, setPaginacao] = useState<MetadadosPaginacao>(paginacaoVazia);

  const carregar = async (pagina = paginacao.pagina) => {
    try {
      const [respostaMembros, respostaAlcadas] = await Promise.all([
        api.get('/equipa/membros', { params: { pagina, limite: paginacao.limite } }),
        api.get('/tenant/configuracoes/alcadas')
      ]);
      setMembros(respostaMembros.data.dados || []);
      setPaginacao(respostaMembros.data.paginacao || paginacaoVazia);
      setAlcadas({ ...alcadasVazias, ...(respostaAlcadas.data.dados || {}) });
    } catch (erroLista: any) {
      setErro(extrairMensagemErro(erroLista, 'Erro ao carregar equipa interna.'));
    }
  };

  useEffect(() => {
    carregar(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const atualizarCampo = (campo: string, valor: string | number) => {
    setForm((atual) => ({ ...atual, [campo]: valor }));
  };

  const atualizarCampoEdicao = (campo: string, valor: string | number) => {
    setEdicao((atual: any) => ({ ...atual, [campo]: valor }));
  };

  const perfilAplicaAlcada = (perfil: string) => perfisComAlcada.has(perfil);

  const prepararPayloadMembro = (dados: any) => ({
    ...dados,
    nivel_alcada: perfilAplicaAlcada(dados.perfil) ? Number(dados.nivel_alcada || 1) : 1
  });

  const criarMembro = async (evento: React.FormEvent) => {
    evento.preventDefault();
    setCarregando(true);
    setErro(null);
    setMensagem(null);

    try {
      const resposta = await api.post('/equipa/membros', prepararPayloadMembro(form));
      setMensagem(resposta.data.mensagem || 'Membro criado com sucesso.');
      setForm(formMembroVazio);
      await carregar(1);
    } catch (erroCriacao: any) {
      setErro(extrairMensagemErro(erroCriacao, 'Erro ao criar membro interno.'));
    } finally {
      setCarregando(false);
    }
  };

  const iniciarEdicao = (membro: any) => {
    setMembroEditandoId(membro.id);
    setEdicao({
      nome: membro.nome,
      email: membro.email,
      perfil: membro.perfil,
      nivel_alcada: membro.nivel_alcada || 1,
      status: membro.status,
      senha: ''
    });
  };

  const salvarEdicao = async () => {
    if (!membroEditandoId) return;
    setErro(null);
    setMensagem(null);

    try {
      const payload = prepararPayloadMembro(edicao);
      if (!payload.senha) delete payload.senha;
      const resposta = await api.put(`/equipa/membros/${membroEditandoId}`, payload);
      setMensagem(resposta.data.mensagem || 'Membro atualizado.');
      setMembroEditandoId(null);
      setEdicao({});
      await carregar();
    } catch (erroEdicao: any) {
      setErro(extrairMensagemErro(erroEdicao, 'Erro ao atualizar membro interno.'));
    }
  };

  const atualizarAlcada = (campo: string, valor: string | number | boolean) => {
    setAlcadas((atual) => ({ ...atual, [campo]: valor }));
  };

  const validarAlcadas = () => {
    const nivel1 = Number(alcadas.alcada_nivel1_max);
    const nivel2 = Number(alcadas.alcada_nivel2_max);
    const nivel3 = Number(alcadas.alcada_nivel3_max);
    const nivel4 = alcadas.nivel4_ilimitado ? LIMITE_NIVEL4_ILIMITADO : Number(alcadas.alcada_nivel4_max);

    if ([nivel1, nivel2, nivel3, nivel4].some((valor) => !Number.isFinite(valor) || valor <= 0)) {
      return 'Todos os limites devem ser valores positivos.';
    }
    if (!(nivel1 <= nivel2 && nivel2 <= nivel3 && nivel3 <= nivel4)) {
      return 'A matriz deve seguir: nível 1 <= nível 2 <= nível 3 <= nível 4.';
    }
    return null;
  };

  const salvarAlcadas = async (evento: React.FormEvent) => {
    evento.preventDefault();
    setErro(null);
    setMensagem(null);

    const erroValidacao = validarAlcadas();
    if (erroValidacao) {
      setErro(erroValidacao);
      return;
    }

    try {
      const resposta = await api.put('/tenant/configuracoes/alcadas', {
        alcada_nivel1_max: Number(alcadas.alcada_nivel1_max),
        alcada_nivel2_max: Number(alcadas.alcada_nivel2_max),
        alcada_nivel3_max: Number(alcadas.alcada_nivel3_max),
        alcada_nivel4_max: Number(alcadas.alcada_nivel4_max),
        nivel4_ilimitado: Boolean(alcadas.nivel4_ilimitado)
      });
      setAlcadas({ ...alcadasVazias, ...(resposta.data.dados || {}) });
      setMensagem(resposta.data.mensagem || 'Matriz de alçadas atualizada.');
    } catch (erroAlcadas: any) {
      setErro(extrairMensagemErro(erroAlcadas, 'Erro ao atualizar matriz de alçadas.'));
    }
  };

  const alternarStatus = async (membro: any) => {
    setErro(null);
    setMensagem(null);

    try {
      const acao = membro.status === 'ATIVO' ? 'desativar' : 'ativar';
      const resposta = await api.post(`/equipa/membros/${membro.id}/${acao}`);
      setMensagem(resposta.data.mensagem || 'Status atualizado.');
      await carregar();
    } catch (erroStatus: any) {
      setErro(extrairMensagemErro(erroStatus, 'Erro ao alterar status do membro.'));
    }
  };

  return (
    <div className="workspace-page">
      <header className="page-header">
        <div>
          <span className="eyebrow">Gerente da empresa</span>
          <h1>Equipa interna</h1>
          <p>Cadastro e ativação de membros do seu tenant.</p>
        </div>
      </header>

      {mensagem && <div className="alerta alerta-sucesso">{mensagem}</div>}
      {erro && <div className="alerta alerta-erro">{erro}</div>}

      <section className="operation-grid">
        <form className="panel supplier-form" onSubmit={criarMembro}>
          <div className="panel-heading">
            <h2>Novo membro</h2>
            <span className="status-pill status-done">Tenant atual</span>
          </div>

          <label>
            Nome
            <input value={form.nome} onChange={(evento) => atualizarCampo('nome', evento.target.value)} required />
          </label>
          <label>
            E-mail
            <input type="email" value={form.email} onChange={(evento) => atualizarCampo('email', evento.target.value)} required />
          </label>
          <label>
            Perfil
            <select value={form.perfil} onChange={(evento) => atualizarCampo('perfil', evento.target.value)}>
              {perfis.map((perfil) => <option key={perfil.valor} value={perfil.valor}>{perfil.label}</option>)}
            </select>
          </label>
          <label>
            Nível de alçada
            <input type="number" min={1} max={4} value={form.nivel_alcada} onChange={(evento) => atualizarCampo('nivel_alcada', Number(evento.target.value))} disabled={!perfilAplicaAlcada(form.perfil)} />
          </label>
          <label>
            Status
            <select value={form.status} onChange={(evento) => atualizarCampo('status', evento.target.value)}>
              <option value="ATIVO">Ativo</option>
              <option value="CONVITE_PENDENTE">Convite pendente</option>
              <option value="INATIVO">Inativo</option>
            </select>
          </label>
          <label>
            Senha inicial
            <input type="password" minLength={6} value={form.senha} onChange={(evento) => atualizarCampo('senha', evento.target.value)} required />
          </label>

          <button className="botao botao-primario" type="submit" disabled={carregando}>
            {carregando ? 'A criar...' : 'Criar membro'}
          </button>
        </form>

        <div className="panel">
          <div className="panel-heading">
            <h2>Membros cadastrados</h2>
            <span className="status-pill">{membros.length}</span>
          </div>
          <div className="table-shell">
            <table>
              <thead>
                <tr><th>Nome</th><th>Perfil</th><th>Status</th><th>Alçada</th><th>Ação</th></tr>
              </thead>
              <tbody>
                {membros.map((membro) => (
                  <tr key={membro.id}>
                    <td>
                      {membroEditandoId === membro.id ? (
                        <>
                          <input value={edicao.nome || ''} onChange={(evento) => atualizarCampoEdicao('nome', evento.target.value)} />
                          <input type="email" value={edicao.email || ''} onChange={(evento) => atualizarCampoEdicao('email', evento.target.value)} />
                        </>
                      ) : (
                        <>{membro.nome}<small>{membro.email}</small></>
                      )}
                    </td>
                    <td>
                      {membroEditandoId === membro.id ? (
                        <select value={edicao.perfil || membro.perfil} onChange={(evento) => atualizarCampoEdicao('perfil', evento.target.value)}>
                          {perfis.map((perfil) => <option key={perfil.valor} value={perfil.valor}>{perfil.label}</option>)}
                        </select>
                      ) : membro.perfil}
                    </td>
                    <td>
                      {membroEditandoId === membro.id ? (
                        <select value={edicao.status || membro.status} onChange={(evento) => atualizarCampoEdicao('status', evento.target.value)}>
                          <option value="ATIVO">Ativo</option>
                          <option value="CONVITE_PENDENTE">Convite pendente</option>
                          <option value="INATIVO">Inativo</option>
                          <option value="SUSPENSO">Suspenso</option>
                        </select>
                      ) : (
                        <span className={`status-pill ${membro.status === 'ATIVO' ? 'status-done' : 'muted'}`}>{membro.status}</span>
                      )}
                    </td>
                    <td>
                      {membroEditandoId === membro.id ? (
                        <input type="number" min={1} max={4} value={edicao.nivel_alcada || 1} onChange={(evento) => atualizarCampoEdicao('nivel_alcada', Number(evento.target.value))} disabled={!perfilAplicaAlcada(edicao.perfil || membro.perfil)} />
                      ) : perfilAplicaAlcada(membro.perfil) ? membro.nivel_alcada : '-'}
                    </td>
                    <td>
                      {membro.perfil === 'GERENTE_EMPRESA' ? (
                        <span className="muted">Gerente atual</span>
                      ) : membroEditandoId === membro.id ? (
                        <div className="button-row">
                          <button className="botao botao-primario" type="button" onClick={salvarEdicao}>Salvar</button>
                          <button className="botao botao-secundario" type="button" onClick={() => { setMembroEditandoId(null); setEdicao({}); }}>Cancelar</button>
                        </div>
                      ) : (
                        <div className="button-row">
                          <button className="botao botao-secundario" type="button" onClick={() => iniciarEdicao(membro)}>Editar</button>
                          <button className="botao botao-secundario" type="button" onClick={() => alternarStatus(membro)}>
                            {membro.status === 'ATIVO' ? 'Desativar' : 'Ativar'}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {membros.length === 0 && <tr><td colSpan={5}>Nenhum membro encontrado.</td></tr>}
              </tbody>
            </table>
            <Paginacao paginacao={paginacao} carregando={carregando} aoMudarPagina={carregar} />
          </div>
        </div>
      </section>

      <section className="panel">
        <form onSubmit={salvarAlcadas}>
          <div className="panel-heading">
            <h2>Matriz de Alçadas</h2>
            <button className="botao botao-primario" type="submit">Salvar matriz</button>
          </div>
          <div className="filters-grid">
            <label>
              Nível 1
              <input type="number" min="1" value={alcadas.alcada_nivel1_max} onChange={(evento) => atualizarAlcada('alcada_nivel1_max', Number(evento.target.value))} />
            </label>
            <label>
              Nível 2
              <input type="number" min="1" value={alcadas.alcada_nivel2_max} onChange={(evento) => atualizarAlcada('alcada_nivel2_max', Number(evento.target.value))} />
            </label>
            <label>
              Nível 3
              <input type="number" min="1" value={alcadas.alcada_nivel3_max} onChange={(evento) => atualizarAlcada('alcada_nivel3_max', Number(evento.target.value))} />
            </label>
            <label>
              Nível 4
              <input type="number" min="1" value={alcadas.nivel4_ilimitado ? LIMITE_NIVEL4_ILIMITADO : alcadas.alcada_nivel4_max} onChange={(evento) => atualizarAlcada('alcada_nivel4_max', Number(evento.target.value))} disabled={alcadas.nivel4_ilimitado} />
            </label>
            <label>
              Nível 4 ilimitado
              <select value={alcadas.nivel4_ilimitado ? 'SIM' : 'NAO'} onChange={(evento) => atualizarAlcada('nivel4_ilimitado', evento.target.value === 'SIM')}>
                <option value="NAO">Não</option>
                <option value="SIM">Sim</option>
              </select>
            </label>
          </div>
          <p className="muted">Atual: N1 {moeda(Number(alcadas.alcada_nivel1_max))} · N2 {moeda(Number(alcadas.alcada_nivel2_max))} · N3 {moeda(Number(alcadas.alcada_nivel3_max))} · N4 {alcadas.nivel4_ilimitado ? 'ilimitado' : moeda(Number(alcadas.alcada_nivel4_max))}</p>
        </form>
      </section>
    </div>
  );
};

export default EquipaInterna;
